import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState, useCallback, useEffect } from 'react';
import { useMarketDetail } from '../hooks/useMarketDetail';
import { Loading } from '../components/common/Loading';
import { MatchHeader } from '../components/detail/MatchHeader';
import { GameInfoTabs } from '../components/detail/GameInfoTabs';
import { PointsUnlockCard } from '../components/detail/PointsUnlockCard';
import { WhaleAnalysis } from '../components/detail/WhaleAnalysis';
import { SmartMoneyPositions } from '../components/detail/SmartMoneyPositions';
import { OddsChart } from '../components/detail/OddsChart';
import { OddsComparison } from '../components/detail/OddsComparison';
import { AIAnalysis } from '../components/detail/AIAnalysis';
import { ValueAnalysis } from '../components/detail/ValueAnalysis';
import { DetailFooter } from '../components/detail/DetailFooter';
// Live game components (Requirements 7.1, 7.2, 7.3, 7.4)
import { LiveScorePanel } from '../components/detail/LiveScorePanel';
import { LiveWinProbabilityChart } from '../components/detail/LiveWinProbabilityChart';
import { PlayByPlayPanel } from '../components/detail/PlayByPlayPanel';
import { LiveBoxScore } from '../components/detail/LiveBoxScore';
// Trading components
import { TradeModal } from '../components/trading';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';

export default function MarketDetailPage() {
  const { marketId } = useParams();
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const [tradeModalOpen, setTradeModalOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const [pointsBalance, setPointsBalance] = useState(null);
  const [pointsPrice, setPointsPrice] = useState(null);
  const [pointsLoading, setPointsLoading] = useState(false);
  const [pointsPurchaseLoading, setPointsPurchaseLoading] = useState(false);

  const pushToast = useCallback((message, type = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const {
    market,
    holders,
    smartMoneyPositions,
    oddsHistory,
    oddsCompare,
    prediction,
    playerProps,
    playerPropsLoading,
    playerPropsError,
    playerPropsAccess,
    loadPlayerProps,
    gameDetail,
    gameId,
    teamStats,
    accessStatus,
    loading,
    error,
    refetch,
    // Live game data (Requirements 7.1, 7.2)
    isLive,
    liveScore,
    lineups,
    // Probability history for win probability chart
    probHistory,
  } = useMarketDetail(marketId);

  const shouldLoadPoints =
    isAuthenticated &&
    accessStatus?.isMembershipRequired &&
    accessStatus?.currentTier === 'free' &&
    !accessStatus?.pointsUnlocked;

  const loadPointsInfo = useCallback(async () => {
    if (!shouldLoadPoints) return;
    setPointsLoading(true);
    try {
      const [balanceRes, productsRes] = await Promise.all([
        api.getPointsBalance(),
        api.getPointsProducts(),
      ]);
      const balance = balanceRes?.data?.balance ?? 0;
      const products = productsRes?.data?.products || [];
      const matchProduct = products.find((p) => p.product_code === 'match_access');
      setPointsBalance(balance);
      setPointsPrice(matchProduct?.price_points ?? null);
    } catch (err) {
      console.error('Failed to load points info:', err);
    } finally {
      setPointsLoading(false);
    }
  }, [shouldLoadPoints]);

  const handlePointsUnlock = useCallback(async () => {
    if (!market?.id) return;
    setPointsPurchaseLoading(true);
    try {
      const result = await api.createPointsPurchase(
        'match_access',
        'market',
        market.id
      );
      if (result.code === 'SUCCESS') {
        pushToast(t('points.unlockSuccess', '点数解锁成功'), 'success');
        await loadPointsInfo();
        await refetch();
      } else if (result.code === 'INSUFFICIENT_POINTS') {
        pushToast(t('points.insufficient', '点数不足'), 'error');
      } else {
        pushToast(result.msg || t('points.unlockFailed', '解锁失败'), 'error');
      }
    } catch (err) {
      pushToast(err?.message || t('points.unlockFailed', '解锁失败'), 'error');
    } finally {
      setPointsPurchaseLoading(false);
    }
  }, [market?.id, loadPointsInfo, pushToast, refetch, t]);

  const handleUpgradeClick = useCallback(() => {
    window.dispatchEvent(
      new CustomEvent('membership-required', {
        detail: { currentTier: accessStatus?.currentTier || 'free' },
      })
    );
  }, [accessStatus?.currentTier]);

  const pointsUnlocked = !!accessStatus?.pointsUnlocked;

  useEffect(() => {
    if (shouldLoadPoints) {
      loadPointsInfo();
    } else {
      setPointsBalance(null);
      setPointsPrice(null);
    }
  }, [shouldLoadPoints, loadPointsInfo]);

  if (loading) return <Loading className="h-96" />;
  if (error) return <div className="text-negative p-8">Error loading market: {error.message}</div>;
  if (!market) return <div className="text-gray-400 p-8">Market not found</div>;

  return (
    <div className="space-y-6 pb-16">
      {/* LIVE Badge Indicator (Requirement 7.3) */}
      {isLive && (
        <div className="flex items-center gap-2 px-4 py-2 bg-negative/10 border border-negative/30 rounded-lg">
          <span className="animate-pulse text-negative">🔴</span>
          <span className="text-negative font-semibold">LIVE</span>
          <span className="text-gray-400 text-sm">{t('live.inProgress', 'Game in progress')}</span>
        </div>
      )}

      {/* Match Header with team logos, odds, home/away indicators */}
      <MatchHeader market={market} prediction={prediction} teamStats={teamStats} />

      <PointsUnlockCard
        isAuthRequired={accessStatus?.isAuthRequired}
        isMembershipRequired={accessStatus?.isMembershipRequired}
        pointsUnlocked={pointsUnlocked}
        currentTier={accessStatus?.currentTier}
        pricePoints={pointsPrice}
        balance={pointsBalance}
        loading={pointsLoading}
        purchaseLoading={pointsPurchaseLoading}
        onUnlock={handlePointsUnlock}
        onUpgrade={handleUpgradeClick}
      />

      {/* Live Game Components - Only render when market.status === 'live' (Requirements 7.1, 7.2, 7.4) */}
      {isLive && (
        <>
          {/* Live Score Panel - uses gameId from balldontlie */}
          <LiveScorePanel 
            eventId={gameId} 
            market={market} 
            isLive={isLive} 
          />

          {/* Live Win Probability Chart */}
          <LiveWinProbabilityChart
            data={probHistory}
            homeTeam={market.teamB}
            awayTeam={market.teamA}
            currentHomeProb={market.priceB ?? 0.5}
            currentAwayProb={market.priceA ?? 0.5}
            probChange={0}
          />

          {/* Play-By-Play Panel */}
          <PlayByPlayPanel
            teamA={market.teamA}
            teamB={market.teamB}
            gameId={gameId}
            isLive={isLive}
          />

          {/* Live Box Score - Full width since LiveLineups is removed */}
          <LiveBoxScore
            eventId={gameId}
            market={market}
            isLive={isLive}
          />
        </>
      )}

      {/* Game Info Tabs - Lineups, Props, H2H, Form, Injuries, Stats */}
      <GameInfoTabs
        market={market}
        gameData={null}
        playerProps={playerProps}
        playerPropsLoading={playerPropsLoading}
        playerPropsError={playerPropsError}
        playerPropsAccess={playerPropsAccess}
        onLoadPlayerProps={loadPlayerProps}
        gameDetail={gameDetail}
        gameId={gameId}
        accessStatus={accessStatus}
      />

      {/* Whale Analysis & Smart Money Positions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <WhaleAnalysis holders={holders} market={market} accessStatus={accessStatus} />
        <SmartMoneyPositions positions={smartMoneyPositions} accessStatus={accessStatus} />
      </div>

      {/* Odds Chart & Comparison */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <OddsChart data={oddsHistory} market={market} accessStatus={accessStatus} />
        <OddsComparison data={oddsCompare} market={market} accessStatus={accessStatus} />
      </div>

      {/* AI Analysis & Value Analysis */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <AIAnalysis prediction={prediction} market={market} accessStatus={accessStatus} />
        <ValueAnalysis prediction={prediction} market={market} accessStatus={accessStatus} />
      </div>

      {/* Fixed Footer with real-time status */}
      <DetailFooter market={market} onTradeClick={() => setTradeModalOpen(true)} />

      {/* Trade Modal */}
      <TradeModal
        open={tradeModalOpen}
        onClose={() => setTradeModalOpen(false)}
        market={market}
        pushToast={pushToast}
      />

      {/* Toast */}
      {toast && (
        <div
          className={`fixed bottom-24 left-1/2 -translate-x-1/2 z-[200] px-4 py-3 rounded-xl shadow-lg ${
            toast.type === 'success'
              ? 'bg-emerald-500/90 text-white'
              : toast.type === 'error'
              ? 'bg-rose-500/90 text-white'
              : 'bg-sky-500/90 text-white'
          }`}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
}
